import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { InventoryItemDto } from '../../interfaces/Inventory/InventoryItemDto'
import { InventoryImportAccordionList } from '../../components/InventoryImport/InventoryImportAccordionList'
//

type IdentificationType = {
  taxIdentificationTypeId: string | number
  abbreviationName: string
}
type TypeShipment = { value: string | number; name: string }
type BusinessUnit = { buCode: string; buName?: string }
type City = { locationCode: string }
type PieceCategory = {
  pieceCategoryId?: string | number
  description?: string
  id?: string | number
  name?: string
}

export type InventoryImportProduct = {
  productCode: string
  productName?: string
  quantity: number
  width?: number
  height?: number
  length?: number
  weight?: number
  declaredValue?: number
  categoryId?: string | number
}

export type InventoryImportOrder = {
  orderNumber: string
  idTypeAbbr: string
  idNumber: string
  name: string
  phone: string
  email: string
  deliveryTypeName: string
  buCode?: string
  cityCode?: string
  address?: string
  postalCode?: string
  notes?: string
  products: InventoryImportProduct[]
}

export type ProductErrors = Partial<
  Record<keyof InventoryImportProduct, string>
>
export type OrderErrors =
  | (Partial<Record<keyof Omit<InventoryImportOrder, 'products'>, string>> & {
      products?: ProductErrors[]
    })
  | {}

const enrichOrderByProducts = (
  o: InventoryImportOrder,
  productsMap: Record<string, InventoryItemDto>
): InventoryImportOrder => {
  const products = (o.products || []).map((prod) => {
    const code = (prod.productCode || '').trim()
    if (!code) return prod
    const p = productsMap[code]
    if (!p) return prod
    return {
      ...prod,
      productName: p.Name,
      length: p.Length,
      width: p.Width,
      height: p.Height,
      weight: p.Weight,
      declaredValue: p.DeclareValues,
    }
  })
  return { ...o, products }
}

const enrichOrderByStore = (
  o: InventoryImportOrder,
  bus: Array<any>
): InventoryImportOrder => {
  if (o.deliveryTypeName !== 'Oficina' || !o.buCode) return o
  const bu = (bus || []).find((b: any) => b?.buCode === o.buCode)
  if (!bu) return o
  const loc = bu.location || {}
  const cityCode = loc.locationCode || bu.locationCode || o.cityCode || ''
  const address = loc.address || bu.address || o.address || ''
  const postalCode = loc.postalCode || bu.postalCode || o.postalCode || ''
  return {
    ...o,
    cityCode,
    address,
    postalCode,
  }
}

const validateOrder = (
  r: InventoryImportOrder,
  catalogs: {
    idTypes: IdentificationType[]
    shipments: TypeShipment[]
    bus: BusinessUnit[]
    cities: City[]
    productsMap: Record<string, InventoryItemDto>
    priorUsage?: Record<string, number>
  }
): OrderErrors => {
  const e: any = {}
  if (!r.orderNumber?.trim()) e.orderNumber = 'Requerido'
  if (!r.idTypeAbbr?.trim()) e.idTypeAbbr = 'Requerido'
  else if (!catalogs.idTypes.some((t) => t.abbreviationName === r.idTypeAbbr))
    e.idTypeAbbr = 'No válido'
  if (!r.idNumber?.trim()) e.idNumber = 'Requerido'
  if (!r.name?.trim()) e.name = 'Requerido'
  if (!r.email?.trim()) e.email = 'Requerido'
  else {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
    if (!emailRegex.test(r.email.trim())) {
      e.email = 'Formato inválido (ej: usuario@dominio.com)'
    }
  }
  if (!r.deliveryTypeName?.trim()) e.deliveryTypeName = 'Requerido'
  else if (!catalogs.shipments.some((s) => s.name === r.deliveryTypeName))
    e.deliveryTypeName = 'No válido'

  if (r.deliveryTypeName === 'Oficina') {
    if (!r.buCode?.trim()) e.buCode = 'Requerido para oficina'
    else if (!catalogs.bus.some((b) => b.buCode === r.buCode))
      e.buCode = 'No válido'
  }
  if (r.deliveryTypeName === 'Domicilio') {
    if (!r.cityCode?.trim()) e.cityCode = 'Requerido para domicilio'
    else if (!catalogs.cities.some((c) => c.locationCode === r.cityCode))
      e.cityCode = 'No válido'
  }
  if (!r.address?.trim()) e.address = 'Dirección requerida.'

  const prodErrors: ProductErrors[] = []
  if (!(r.products || []).length) {
    // Si la orden no tiene ningún producto, marcar error sintético en lista de productos
    prodErrors.push({ productCode: 'Debe agregar al menos un producto' })
  } else {
    const currentUsageCheck = { ...(catalogs.priorUsage || {}) }

    for (const p of r.products || []) {
      const pe: ProductErrors = {}
      const code = p.productCode?.trim()
      if (!code) pe.productCode = 'Requerido'
      else if (!catalogs.productsMap[code])
        pe.productCode = 'Producto requerido'

      const qty = p.quantity || 0
      if (!qty || qty <= 0) pe.quantity = 'Debe ser mayor a 0'
      else if (code && catalogs.productsMap[code]) {
        const max = catalogs.productsMap[code].Count ?? 0
        const used = currentUsageCheck[code] || 0

        if (used + qty > max) {
          const available = Math.max(0, max - used)
          pe.quantity = `Solo quedan ${available} disponibles (solicitado: ${qty})`
        }

        currentUsageCheck[code] = used + qty
      }

      if (p.declaredValue != null && (p.declaredValue as any) <= 0)
        pe.declaredValue = 'Debe ser mayor a 0'
      if (!p.categoryId && p.categoryId !== 0) pe.categoryId = 'Requerido'
      if (p.length === 0) pe.length = 'La medida largo no puede ser 0'
      if (p.width === 0) pe.width = 'La medida ancho no puede ser 0'
      if (p.height === 0) pe.height = 'La medida alto no puede ser 0'
      if (p.weight === 0) pe.weight = 'El peso no puede ser 0'
      prodErrors.push(pe)
    }
  }
  e.products = prodErrors
  return e as OrderErrors
}

const InventoryImportReview: FC<{
  rows: InventoryImportOrder[]
  identificationTypes: IdentificationType[]
  typeShipment: TypeShipment[]
  businessUnits: BusinessUnit[]
  cities: City[]
  pieceCategories: PieceCategory[]
  onClose: () => void
  onProcess?: (rows: InventoryImportOrder[]) => void
  onRowsChange?: (rows: InventoryImportOrder[]) => void
  onHasErrorsChange?: (hasErrors: boolean) => void
  inventoryItems?: InventoryItemDto[]
}> = ({
  rows,
  identificationTypes,
  typeShipment,
  businessUnits,
  cities,
  pieceCategories,
  onClose,
  onProcess,
  onRowsChange,
  onHasErrorsChange,
  inventoryItems,
}) => {
  const [localRows, setLocalRows] = useState<InventoryImportOrder[]>(rows || [])
  const [errors, setErrors] = useState<OrderErrors[]>([])
  // Keep stable refs for callback props to avoid effect loops on identity changes
  const onRowsChangeRef = useRef(onRowsChange)
  const onHasErrorsChangeRef = useRef(onHasErrorsChange)

  useEffect(() => {
    onRowsChangeRef.current = onRowsChange
  }, [onRowsChange])
  useEffect(() => {
    onHasErrorsChangeRef.current = onHasErrorsChange
  }, [onHasErrorsChange])

  const productsMap = useMemo(() => {
    const map: Record<string, InventoryItemDto> = {}
    ;(inventoryItems || []).forEach((it) => {
      if (it?.Id) map[it.Id] = it
    })
    return map
  }, [inventoryItems])

  const productOptions = useMemo(() => {
    return (inventoryItems || []).map((it) => ({
      id: it.Id,
      code: it.Id,
      name: `${it.Id} · ${it.Name}`,
      Count: it.Count,
    }))
  }, [inventoryItems])

  const usagePerOrder = useMemo(() => {
    const running: Record<string, number> = {}
    return (localRows || []).map((r) => {
      const prior = { ...running }
      ;(r.products || []).forEach((p) => {
        const c = p.productCode?.trim()
        if (c && p.quantity) {
          running[c] = (running[c] || 0) + p.quantity
        }
      })
      return prior
    })
  }, [localRows])

  useEffect(() => {
    // Enrich incoming orders by product code/info using inventoryItems
    const initial = (rows || [])
      .map((r) => enrichOrderByProducts(r, productsMap))
      .map((r) => enrichOrderByStore(r, businessUnits as any))
    setLocalRows(initial)
    onRowsChangeRef.current?.(initial)
  }, [rows, productsMap, businessUnits])

  useEffect(() => {
    const runningUsage: Record<string, number> = {}

    const errs = (localRows || []).map((r) => {
      const priorUsageForThisOrder = { ...runningUsage }

      const result = validateOrder(r, {
        idTypes: identificationTypes,
        shipments: typeShipment,
        bus: businessUnits,
        cities,
        productsMap,
        priorUsage: priorUsageForThisOrder,
      })

      ;(r.products || []).forEach((p) => {
        const c = p.productCode?.trim()
        if (c && p.quantity) {
          runningUsage[c] = (runningUsage[c] || 0) + p.quantity
        }
      })

      return result
    })
    setErrors(errs)
    onRowsChangeRef.current?.(localRows)
    // hasErrors if any order has any order-level error or any product error
    const hasErr = errs.some((e) => {
      const orderHas = Object.keys({ ...(e as any) }).some(
        (k) => k !== 'products'
      )
      const prodHas = (e as any)?.products?.some(
        (pe: any) => Object.keys(pe || {}).length > 0
      )
      return orderHas || prodHas
    })
    onHasErrorsChangeRef.current?.(hasErr)
  }, [
    localRows,
    identificationTypes,
    typeShipment,
    businessUnits,
    cities,
    productsMap,
  ])

  // derive errors at parent via onHasErrorsChange

  const updateOrder = (index: number, patch: Partial<InventoryImportOrder>) => {
    setLocalRows((prev) => {
      const next = [...prev]
      let merged = { ...next[index], ...patch }
      // If switching or setting Oficina + tienda, auto-derive city/address/postal
      if (merged.deliveryTypeName === 'Oficina' && merged.buCode) {
        merged = enrichOrderByStore(merged, businessUnits as any)
      }
      next[index] = merged
      return next
    })
  }

  const updateProduct = (
    orderIndex: number,
    productIndex: number,
    patch: Partial<InventoryImportProduct>
  ) => {
    setLocalRows((prev) => {
      const next = [...prev]
      const order = { ...next[orderIndex] }
      const prods = [...(order.products || [])]
      const merged = { ...prods[productIndex], ...patch }
      // Enrich when productCode changes
      if (Object.prototype.hasOwnProperty.call(patch, 'productCode')) {
        const enrichedOrder = enrichOrderByProducts(
          {
            ...order,
            products: prods.map((p, i) => (i === productIndex ? merged : p)),
          },
          productsMap
        )
        next[orderIndex] = enrichedOrder
      } else {
        prods[productIndex] = merged
        next[orderIndex] = { ...order, products: prods }
      }
      return next
    })
  }

  const addProduct = (orderIndex: number) => {
    setLocalRows((prev) => {
      const next = [...prev]
      const order = { ...next[orderIndex] }
      const prods = [...(order.products || [])]
      prods.push({
        productCode: '',
        productName: '',
        quantity: 0,
        length: undefined,
        width: undefined,
        height: undefined,
        weight: undefined,
        declaredValue: undefined,
        categoryId: undefined,
      })
      next[orderIndex] = { ...order, products: prods }
      return next
    })
  }

  const removeProduct = (orderIndex: number, productIndex: number) => {
    setLocalRows((prev) => {
      const next = [...prev]
      const order = { ...next[orderIndex] }
      const prods = [...(order.products || [])]
      if (productIndex >= 0 && productIndex < prods.length) {
        prods.splice(productIndex, 1)
      }
      next[orderIndex] = { ...order, products: prods }
      return next
    })
  }

  const removeOrder = (orderIndex: number) => {
    setLocalRows((prev) => {
      const next = [...prev]
      if (orderIndex >= 0 && orderIndex < next.length) {
        next.splice(orderIndex, 1)
      }
      return next
    })
  }

  return (
    <div className="w-full">
      <InventoryImportAccordionList
        orders={localRows}
        errors={errors}
        identificationTypes={identificationTypes as any}
        typeShipment={typeShipment as any}
        businessUnits={businessUnits as any}
        cities={cities as any}
        pieceCategories={pieceCategories as any}
        onChangeOrder={(idx: number, patch: Partial<InventoryImportOrder>) =>
          updateOrder(idx, patch)
        }
        onChangeProduct={(
          oIdx: number,
          pIdx: number,
          patch: Partial<InventoryImportProduct>
        ) => updateProduct(oIdx, pIdx, patch)}
        onAddProduct={(oIdx: number) => addProduct(oIdx)}
        onRemoveProduct={(oIdx: number, pIdx: number) =>
          removeProduct(oIdx, pIdx)
        }
        onRemoveOrder={(idx: number) => removeOrder(idx)}
        productsMap={productsMap}
        productOptions={productOptions as any}
        usagePerOrder={usagePerOrder}
      />
    </div>
  )
}

export default InventoryImportReview
